<script>
/**
 * ItemAutocomplete
 * @description
 * JWNET取扱品目Autocomplete コンポーネント
 */
export default {
  name: "ItemAutocomplete",
  props: {
    /**
     * 値(v-modelで指定)
     * @type { Array | String | Number | null }
     * @default null
     */
    value: {
      type: [Array, String, Number, null],
      default: null,
    },
    /**
     * 選択項目の表示をチップに変更
     * @type { Boolean }
     * @default false
     */
    chips: {
      type: Boolean,
      default: false,
    },
    /**
     * 選択中のチップに削除アイコンを追加します（chipsオプションと併用）
     * @type { Boolean }
     * @default false
     */
    deletableChips: {
      type: Boolean,
      default: false,
    },
    /**
     * inputの高さを低くする
     * @type { Boolean }
     * @default true
     */
    dense: {
      type: Boolean,
      default: true,
    },
    /**
     * disabled
     * @type { Boolean }
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * クリアアイコンの表示
     * @type { Boolean }
     * @default false
     */
    clearable: {
      type: Boolean,
      default: false,
    },
    /**
     * エラーメッセージ
     * @type { Array }
     * @default []
     */
    errorMessages: {
      type: Array,
      default: () => [],
    },
    /**
     * ヒントやバリデーションエラーを非表示にします。
     * auto が設定されている場合、表示するメッセージ（ヒント、エラーメッセージ、カウンター値など）がある場合のみ表示します
     * @type { String | Boolean }
     * @default undefined
     */
    hideDetails: {
      type: [String, Boolean],
      default: undefined
    },
    /**
     * 選択肢
     * @type { Array }
     * @default []
     */
    items: {
      type: Array,
      default: () => [],
    },
    /**
     * ラベル
     * @type { String }
     * @default null
     */
    label: {
      type: String,
      default: null,
    },
    /**
     * 複数選択
     * @type { Boolean }
     * @default false
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * アウトラインスタイルを適用
     * @type { Boolean }
     * @default false
     */
    outlined: {
      type: Boolean,
      default: false,
    },
    /**
     * プレースホルダーを設定します
     *
     * @type { String }
     * @default null
     */
    placeholder: {
      type: String,
      default: null
    },
    /**
     * プレースホルダーを常に表示
     * @type { Boolean }
     * @default false
     */
    persistentPlaceholder: {
      type: Boolean,
      default: false,
    },
    /**
     * 入力欄に角丸を追加
     * @type { Boolean }
     * @default false
     */
    rounded: {
      type: Boolean,
      default: false,
    },
    /**
     * 選択範囲の表示を small プロパティでchipに変更します
     * @type { Boolean }
     * @default false
     */
    smallChips: {
      type: Boolean,
      default: false,
    },
    /**
     * style属性
     * @type { String }
     * @default null
     */
    cssStyle: {
      type: String,
      default: null,
    },
    /**
     * class属性
     * @type { String }
     * @default null
     */
    classes: {
      type: String,
      default: null,
    },
    /**
     * 特定産業廃棄物のラベル
     *
     * @type { String }
     * @default 特定産業廃棄物（放射性物質）
     */
    specificCaption: {
      type: String,
      default: '特定産業廃棄物（放射性物質）'
    },
  },
  data() {
    return {
    }
  },
  watch: {

  },
  computed: {
    internalValue() {
      // multiple=true の場合は常に Array に変換
      if (this.multiple) {
        if (Array.isArray(this.value)) {
          return this.value
        }
        if (
          this.value === null ||
          this.value === undefined ||
          this.value === ''
        ) {
          return []
        }
        return [this.value]
      }

      // multiple=false の場合
      if (Array.isArray(this.value)) {
        return this.value[0] ?? null
      }
      return this.value
    },
    /**
     * 特定産業廃棄物を選択した際にラベルを表示するか
     * @type { Boolean }
     * @default false
     */
    showSpecificLabel () {
      if (this.multiple) {
        return false;
      }
      let selectedId = this.value;
      if (Array.isArray(this.value)) {
        selectedId = this.value[0]
      }
      const selectedMember = this.items.find(v => v.value === selectedId)
      return ['specific_industrial', 'specific_special_industrial'].includes(selectedMember?.waste_type);
    }
  },
  created() {
  },
  methods: {

    /**
     * v-autocomplete の入力値変更時イベント
     *
     * multiple の設定有無に応じて emit する値を変換する。
     *
     * - multiple=true  : Array を emit
     * - multiple=false : 単一値を emit
     *
     * @param {Array|string|number|null} val
     */
    onInput(val) {
      // multiple=true → Array
      if (this.multiple) {
        this.$emit('input', val)
        this.changeEvent()
        return
      }

      // multiple=false → 単一値
      if (Array.isArray(val)) {
        this.$emit('input', val[0] ?? null)
        return
      }
      this.$emit('input', val)
    },

    /**
     * v-autocomplete の変更イベント
     *
     * 値変更確定時に change イベントを親コンポーネントへ通知する。
     */
    changeEvent () {
      this.$emit('change')
    },

    /**
     * 指定した item が選択済みか判定する。
     *
     * multiple=true の場合は配列内に存在するか確認し、
     * multiple=false の場合は単一値と比較する。
     *
     * @param {Object} item
     * @returns {boolean}
     */
    isSelected(item) {
      if (this.multiple) {
        return Array.isArray(this.internalValue)
          && this.internalValue.includes(item.value)
      }
      return this.internalValue === item.value
    },

    /**
     * 指定した item の選択状態を切り替えた値を返す。
     * @param {Object} item
     * @returns {Array|string|number|null}
     */
    toggleSelection(item) {
      // multiple=true の場合は配列として切り替え
      if (this.multiple) {
        const values = Array.isArray(this.internalValue) ? [...this.internalValue] : []
        const index = values.indexOf(item.value)
        if (index >= 0) {
          values.splice(index, 1)
        } else {
          values.push(item.value)
        }
        return values
      }
      // multiple=false の場合は単一値切り替え
      return this.internalValue === item.value ? null : item.value
    },

    /**
     * v-chipに表示するラベルを返す。
     *
     * @param { String } waste_type
     * @return { String }
     */
    selectorLabel(waste_type) {
      let label = null;
      switch (waste_type) {
        case "industrial":
        case "special_industrial":
          label= "産業廃棄物"
          break;
        case "specific_industrial":
        case "specific_special_industrial":
          label = "特定産業廃棄物"
          break;
        case "general":
          label = "一般廃棄物"
          break;
        default:
          label = null;
      }
      return label;
    },

    /**
     * v-chipの背景色を返す。
     *
     * @param waste_type
     * @return {null}
     */
    selectorColor (waste_type) {
      let label = null;
      switch (waste_type) {
        case "industrial":
        case "special_industrial":
          label= "primary"
          break;
        case "specific_industrial":
        case "specific_special_industrial":
          label = "orange"
          break;
        case "general":
          label = "grey"
      }
      return label;
    },
    onKeydown(e) {
      if (
        this.multiple &&
        e.key === 'Backspace' &&
        e.target.value === ''
      ) {
        e.preventDefault()
        e.stopImmediatePropagation?.()
        e.stopPropagation()
      }
    }
  },
}
</script>

<template>
  <div>
    <v-autocomplete
      ref="autoComplete"
      :chips="chips"
      :class="classes"
      :clearable="clearable"
      :deletable-chips="deletableChips"
      :dense="dense"
      :disabled="disabled"
      :error-messages="errorMessages"
      :hide-details="hideDetails"
      :items="items"
      :label="label"
      :multiple="multiple"
      :outlined="outlined"
      :persistent-placeholder="persistentPlaceholder"
      :placeholder="placeholder"
      :rounded="rounded"
      :small-chips="smallChips"
      :style="cssStyle"
      :value="internalValue"
      @input="onInput"
      @change="changeEvent"
      @keydown.native.capture="onKeydown"
    >
      <template v-slot:item="{ item, on, attrs }">
        <v-list-item
          v-bind="attrs"
          v-on="on"
          dense
        >
          <v-list-item-action
            v-if="multiple"
            class="mr-4"
          >
            <v-simple-checkbox
              :value="isSelected(item)"
              :input-value="isSelected(item)"
              :disabled="item.disabled"
              @click.stop="onInput(toggleSelection(item))"
            />
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>
              <!-- 一廃回収の受注登録の場合はv-chipを表示させないための判定を追加 -->
              <v-chip
                v-if="selectorLabel(item.waste_type)"
                :title="selectorLabel(item.waste_type)"
                :color="selectorColor(item.waste_type)"
                class="mr-1 d-inline-flex align-center justify-center"
                dark
                x-small
              >
                {{ selectorLabel(item.waste_type) }}
              </v-chip>
              {{ item.code }}:{{ item.text }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-slot:message>
      </template>
    </v-autocomplete>
    <p
      v-if="showSpecificLabel"
      class="ml-3 orange--text font-weight-bold"
    >
      {{ specificCaption }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.target-items-select ::v-deep .v-chip {
  background-color: white !important;
  border: 1px solid #9e9e9e;
}

.target-items-select ::v-deep .v-chip .v-chip__content {
  color: rgba(0, 0, 0, 0.87);
}

.target-items-select ::v-deep .v-select__selections {
  margin-top: 5px;
  & .v-chip--select {
    margin-top: 3px;
  }
}

</style>
